import React, { useEffect, useState } from "react";
import apiRequest from "../../../helpers/apiRequest";
import { Urls } from "../../../utils/urls";
import Loading from "../../../components/loading/Loading";
import AddSubjectModal from "./AddSubjectModal";
import SubjectDetailsModal from "./SubjectDetailsModal";
import { useAppProvider } from "../../../providers/AppProvider";
import ExamsSidebar from "./ExamsSidebar";
import ExamsMainContent from "./ExamsMainContent";
import EditExamModal from "./EditExamModal";

export type TExam = {
  _id: string;
  name: string;
  subjects: TSub[];
};

export type TUnit = {
  name: string;
  _id: string;
  subject: string;
};

export type TSub = {
  name: string;
  _id: string;
  units?: TUnit[];
};

type TLoadings = {
  addExam?: boolean;
  addSub?: boolean;
  deleteExam?: boolean;
  deleteSubject?: boolean;
};

type TModals = {
  addSubject: boolean;
  subjectDetails: undefined | TSub;
  updateSubject?: undefined | TSub;
};

const ExamsConfigs = () => {
  const { exams, setExams } = useAppProvider()!;
  const [loading, setLoading] = useState(true);
  const [loadings, setLoadings] = useState<TLoadings>({});
  const [selExam, setSelExam] = useState<TExam | null>(null);
  const [examName, setExamName] = useState<string>("");
  const [editingExam, setEditingExam] = useState<TExam | null>(null);
  const [editExamName, setEditExamName] = useState<string>("");
  const [modals, setModals] = useState<TModals>({
    addSubject: false,
    subjectDetails: undefined,
  });

  const getAllExams = async () => {
    const { data, error } = await apiRequest<{ exams: TExam[] }>({
      method: "GET",
      url: Urls.all_exams,
    });

    if (error) {
      alert(error);
    } else if (data) {
      setLoading(false);
      setExams([...data.exams]);

      if (data.exams.length > 0) {
        setSelExam((prev) => {
          if (!prev) return data.exams[0];
          return data.exams.find((e) => e._id === prev._id) || data.exams[0];
        });
      }
    }
  };

  const onAddExam = async () => {
    if (!examName) return alert("Please enter an exam name");
    setLoadings((prev) => ({ ...prev, addExam: true }));

    const { data, error } = await apiRequest<any>({
      method: "POST",
      url: Urls.all_exams,
      data: { name: examName },
    });

    if (error) {
      alert(error);
    } else if (data) {
      setExamName("");
      await getAllExams();
    }

    setLoadings((prev) => ({ ...prev, addExam: false }));
  };

  const onUpdateExam = async () => {
    if (!editingExam || !editExamName) return;
    setLoadings((prev) => ({ ...prev, addExam: true }));

    const { data, error } = await apiRequest<any>({
      method: "PATCH",
      url: `${Urls.exam}/${editingExam._id}`,
      data: { name: editExamName },
    });

    if (error) {
      alert(error);
    } else if (data) {
      setEditingExam(null);
      await getAllExams();
    }

    setLoadings((prev) => ({ ...prev, addExam: false }));
  };

  const onDeleteExam = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const confirm = window.confirm("Are you sure you want to delete this exam?");
    if (!confirm) return;

    setLoadings((prev) => ({ ...prev, deleteExam: true }));

    const { data, error } = await apiRequest<any>({
      method: "DELETE",
      url: `${Urls.all_exams}/${id}`,
    });

    if (error) {
      alert(error);
    } else if (data) {
      if (selExam?._id === id) setSelExam(null);
      setExams(exams?.filter((a) => a._id !== id) ?? []);
    }

    setLoadings((prev) => ({ ...prev, deleteExam: false }));
  };

  const onDeleteSubject = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const confirm = window.confirm("Are you sure you want to delete this subject?");
    if (!confirm) return;

    setLoadings((prev) => ({ ...prev, deleteSubject: true }));

    const { data, error } = await apiRequest<any>({
      method: "DELETE",
      url: `${Urls.subjects}/${id}`,
    });

    if (error) {
      alert(error);
    } else if (data) {
      setSelExam((prev) => prev && {
        ...prev,
        subjects: prev.subjects.filter((a) => a._id !== id),
      });
    }

    setLoadings((prev) => ({ ...prev, deleteSubject: false }));
  };

  useEffect(() => {
    if (!modals.addSubject && !modals.updateSubject) {
      getAllExams();
    }
  }, [modals.addSubject, modals.updateSubject]);

  if (loading) return <Loading />;

  return (
    <div className="h-full flex gap-6">
      <ExamsSidebar
        exams={exams || []}
        selExam={selExam}
        setSelExam={setSelExam}
        examName={examName}
        setExamName={setExamName}
        onAddExam={onAddExam}
        onEditExam={(exam) => {
          setEditingExam(exam);
          setEditExamName(exam.name);
        }}
        onDeleteExam={onDeleteExam}
        isAddingExam={!!loadings.addExam}
      />

      <ExamsMainContent
        selExam={selExam}
        onAddSubject={() => setModals((p) => ({ ...p, addSubject: true }))}
        onUpdateSubject={(sub) => setModals((p) => ({ ...p, updateSubject: sub }))}
        onDeleteSubject={onDeleteSubject}
        onViewSubject={(sub) => setModals((p) => ({ ...p, subjectDetails: sub }))}
      />

      <AddSubjectModal
        isOpen={modals.addSubject || modals.updateSubject !== undefined}
        onClose={() => setModals((p) => ({ ...p, addSubject: false, updateSubject: undefined }))}
        examId={selExam?._id!}
        examName={selExam?.name!}
        selSubject={modals.updateSubject}
      />

      {modals.subjectDetails && (
        <SubjectDetailsModal
          isOpen={!!modals.subjectDetails}
          onClose={() => setModals((p) => ({ ...p, subjectDetails: undefined }))}
          subject={modals.subjectDetails}
          onEdit={() => {
            const sub = modals.subjectDetails;
            setModals((p) => ({ ...p, subjectDetails: undefined, updateSubject: sub }));
          }}
        />
      )}

      <EditExamModal
        isOpen={!!editingExam}
        onClose={() => setEditingExam(null)}
        editExamName={editExamName}
        setEditExamName={setEditExamName}
        onUpdateExam={onUpdateExam}
        loading={!!loadings.addExam}
      />
    </div>
  );
};

export default ExamsConfigs;